import React from 'react';
import { FileText, Users, CheckCircle } from 'lucide-react';
import Navbar from '../../components/Navbar/Navbar';

const Guidelines = () => {
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-teal-400 to-indigo-500 px-8 py-6">
          <h1 className="text-3xl font-bold text-white mb-2">
            Blog Writing Guidelines for FEDGEN-PHIS Authors
          </h1>
          <div className="flex items-center text-white text-sm space-x-4">
            <span>Version 1.0</span>
            <span>•</span>
            <span>Date: 10/10/2025</span>
          </div>
          <p className="text-white/90 text-sm mt-2">
            Author: Emmanuel Adetiba, Ph.D (PI, FEDGEN Research Group)
          </p>
        </div>

        {/* Introduction */}
        <div className="px-8 py-6 border-b border-gray-200">
          <p className="text-gray-700 leading-relaxed">
            This template is a guideline for authoring blog posts on the FEDGEN-PHIS platform. 
            The overarching objective of authoring blogs on FEDGEN-PHIS is to translate complex 
            research findings into an engaging, easy-to-understand piece that is easily accessible 
            to the general public. Your blog must be <strong>concise (i.e. 2 pages or 1000 words maximum)</strong> and 
            focused on the topic being addressed, especially as it relates to new research findings.
          </p>
        </div>

        {/* Guidelines Content */}
        <div className="px-8 py-6 space-y-8">
          
          {/* Section 1: Title */}
          <div className="border-l-4 border-teal-500 pl-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">
              1. Title <span className="text-sm font-normal text-gray-600">(Word Count ~18 words)</span>
            </h2>
            <p className="text-gray-700">
              Create a catchy and engaging title with technical terms that is accessible to non-technical audience.
            </p>
          </div>

          {/* Section 2: Authors */}
          <div className="border-l-4 border-teal-500 pl-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">2. Author(s)</h2>
            <p className="text-gray-700 mb-2">The format for listing author(s) is as follows:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
              <li>Author(s) FirstName, Middle Name (optional) and Surname(s)</li>
              <li>Educational Credentials (optional)</li>
              <li>Affiliations of Author(s)</li>
            </ul>
          </div>

          {/* Section 3: Introduction */}
          <div className="border-l-4 border-teal-500 pl-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">
              3. Introduction <span className="text-sm font-normal text-gray-600">(Word Count: ~150 words)</span>
            </h2>
            <p className="text-gray-700">
              Start with a relatable story or a simple explanation of the health issue. 
              Explain why the topic is important for your readers, public or precision health.
            </p>
          </div>

          {/* Section 4: Main Story/Body */}
          <div className="border-l-4 border-indigo-500 pl-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">
              4. Main Story/Body <span className="text-sm font-normal text-gray-600">(Word Count: ~400-600 words)</span>
            </h2>
            <p className="text-gray-700 mb-3">
              This is the heart of the blog article. Explain the key findings using simple terms, 
              analogies, and stories. Avoid deep technical details. Focus on the "what" and the "why." 
              Use visuals like infographics, pictures and simple diagrams and align to the following structure:
            </p>
            <ul className="space-y-2 text-gray-700 ml-4">
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-indigo-500 mr-2 flex-shrink-0 mt-0.5" />
                <span><strong>Subheading for Finding 1:</strong> (e.g., "A 'Smarter' Tool for Gene Editing")</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-indigo-500 mr-2 flex-shrink-0 mt-0.5" />
                <span><strong>Subheading for Finding 2:</strong> (e.g., "Fixing the Problem in in-Silico")</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-indigo-500 mr-2 flex-shrink-0 mt-0.5" />
                <span><strong>Subheading for Finding n:</strong> (e.g., "Fixing the Problem in Lab-Grown Cells")</span>
              </li>
            </ul>
          </div>

          {/* Section 5: Implications */}
          <div className="border-l-4 border-indigo-500 pl-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">
              5. Implications for Either Public or Precision Health <span className="text-sm font-normal text-gray-600">(Word Count: ~150 words)</span>
            </h2>
            <p className="text-gray-700 mb-3">
              Connect the research directly to your readers/public or precision health. 
              What are the potential impacts on health, policy, or daily life? 
              Provide actionable advice if applicable and provide disclaimer to discourage 
              self-help without professional medical advice/consultations.
            </p>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <p className="text-sm text-gray-700">
                <strong>Example:</strong> "While this research is exciting, it's still in the laboratory phase. 
                It will be a few years before this could become a treatment in hospitals. 
                So adhere to diagnostic and treatment based on professional medical advice/consultation"
              </p>
            </div>
          </div>

          {/* Section 6: Conclusion */}
          <div className="border-l-4 border-teal-500 pl-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">
              6. Conclusion <span className="text-sm font-normal text-gray-600">(Word Count: ~120 words)</span>
            </h2>
            <p className="text-gray-700">
              Summarize the main takeaway in two or three simple sentences. 
              Ensure you end with an empowering and forward-looking message.
            </p>
          </div>

          {/* Section 7: References */}
          <div className="border-l-4 border-teal-500 pl-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">7. References</h2>
            <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
              <li>Link to the original scientific paper</li>
              <li>Link to the official university press release about the study, professional/organisational platforms</li>
            </ul>
          </div>

          {/* Section 8: Disclosures */}
          <div className="border-l-4 border-red-500 pl-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">8. Disclosures</h2>
            <p className="text-gray-700 mb-2">
              Transparency is key for public trust, so, state any potential conflicts of interest.
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded p-4">
              <p className="text-sm text-gray-700">
                If none exist, state: <strong>"The author(s) declare no conflicts of interest."</strong>
              </p>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="bg-gray-100 px-8 py-4 border-t border-gray-200">
          <p className="text-sm text-gray-600 text-center">
            For questions or clarifications, please contact the FEDGEN-PHIS Editorial Team
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default Guidelines;